import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { CommunicationRadar } from '@/components/dashboard/CommunicationRadar';
import { StatCard } from '@/components/dashboard/StatCard';
import { DominantTraits } from '@/components/dashboard/DominantTraits';
import { ClarityAnalysis } from '@/components/dashboard/ClarityAnalysis';
import { CommunicationsTable } from '@/components/dashboard/CommunicationsTable';
import { AdminExecutiveSummary } from '@/components/dashboard/AdminExecutiveSummary';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorDisplay } from '@/components/common/ErrorDisplay';
import { adminApi } from '@/services/api';

const AdminUserAnalytics: React.FC = () => {
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'user-analytics', userId],
    queryFn: async () => {
      const response = await adminApi.getUserAnalyses(userId!, { per_page: 1000 });
      return response.data?.data;
    },
    enabled: !!userId,
    retry: 2,
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner size="lg" message="Loading user analytics..." showTimeoutMessage={true} />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <ErrorDisplay
            type="network"
            title="Unable to Load Analytics"
            message="We couldn't load analytics for this user."
            onRetry={() => refetch()}
            onGoBack={() => navigate('/admin/users')}
            showRetry={true}
            showGoBack={true}
          />
        </div>
      </Layout>
    );
  }

  const analyses = data?.analyses || [];
  const userInfo = data?.user || { name: '', email: '' };
  const totalAnalyses = analyses.length;

  // Calculate sentiment distribution
  const calculateSentimentDistribution = () => {
    if (analyses.length === 0) return { positive: 0, neutral: 0, negative: 0 };

    let totalPositive = 0;
    let totalNeutral = 0;
    let totalNegative = 0;

    analyses.forEach((analysis: any) => {
      const sentiment = analysis.sentiment;
      if (sentiment) {
        totalPositive += sentiment.positivity || 0;
        totalNeutral += sentiment.neutral || 0;
        totalNegative += sentiment.negativity || 0;
      }
    });

    return {
      positive: Math.round((totalPositive / analyses.length) * 100),
      neutral: Math.round((totalNeutral / analyses.length) * 100),
      negative: Math.round((totalNegative / analyses.length) * 100),
    };
  };

  const getAveragePositivity = () => {
    if (analyses.length === 0) return 0;
    const total = analyses.reduce((sum: number, a: any) => sum + (a.sentiment?.positivity || 0), 0);
    return Math.round((total / analyses.length) * 100);
  };

  const sentimentDistribution = calculateSentimentDistribution();
  const averagePositivity = getAveragePositivity();

  const getDominantSentiment = () => {
    const { positive, neutral, negative } = sentimentDistribution;
    if (positive >= neutral && positive >= negative) {
      return { label: 'Positive', value: positive, subtitle: `${neutral}% Neutral, ${negative}% Negative` };
    } else if (neutral >= positive && neutral >= negative) {
      return { label: 'Neutral', value: neutral, subtitle: `${positive}% Positive, ${negative}% Negative` };
    } else {
      return { label: 'Negative', value: negative, subtitle: `${positive}% Positive, ${neutral}% Neutral` };
    }
  };

  const dominantSentiment = getDominantSentiment();

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Navigation */}
        <div className="mb-4">
          <Link
            to="/admin/users"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <span className="material-symbols-outlined text-xl mr-1">arrow_back</span>
            Back to Users
          </Link>
        </div>

        {/* Admin Banner */}
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-amber-600">admin_panel_settings</span>
            <span className="text-amber-800 font-medium">
              Viewing analytics for: {userInfo.name || userInfo.email}
            </span>
            {userInfo.name && userInfo.email && (
              <span className="text-amber-600 text-sm">({userInfo.email})</span>
            )}
          </div>
          <div className="mt-2 flex gap-3">
            <Link
              to={`/admin/users/${userId}/history`}
              className="text-sm text-amber-700 hover:text-amber-900 underline"
            >
              View analysis history
            </Link>
          </div>
        </div>

        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Analytics Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600">
            Communication patterns and insights for {userInfo.name || userInfo.email}
          </p>
        </div>

        {/* Empty State */}
        {totalAnalyses === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8 md:p-12 text-center border border-gray-100">
            <h3 className="mt-4 text-lg sm:text-xl font-semibold text-gray-900">No analyses yet</h3>
            <p className="mt-2 text-sm sm:text-base text-gray-600 max-w-md mx-auto">
              This user has not analysed any communications yet.
            </p>
          </div>
        )}

        {/* Analytics Content */}
        {totalAnalyses > 0 && (
          <>
            {/* Executive Summary */}
            <div className="mb-8">
              <AdminExecutiveSummary userId={userId!} userLabel={userInfo.name || userInfo.email || ''} />
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <StatCard
                title="Total Analyses"
                value={totalAnalyses}
                subtitle="All time"
                icon={
                  <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
              />

              <StatCard
                title="Sentiment Balance"
                value={`${dominantSentiment.value}% ${dominantSentiment.label}`}
                subtitle={dominantSentiment.subtitle}
                icon={
                  <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                }
              />

              <StatCard
                title="Average Positivity"
                value={`${averagePositivity}%`}
                subtitle="Across all communications"
                icon={
                  <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                }
              />
            </div>

            {/* Radar Chart */}
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-100">
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Communication Patterns</h2>
                <p className="text-xs sm:text-sm text-gray-600">
                  Emotional communication profile across 8 key dimensions
                </p>
              </div>
              <CommunicationRadar analyses={analyses} />
            </div>

            {/* Dominant Traits and Clarity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <DominantTraits analyses={analyses} />
              <ClarityAnalysis analyses={analyses} />
            </div>

            {/* Communications Table */}
            <div className="mb-8">
              <CommunicationsTable analyses={analyses} analysisLinkPrefix="/admin/analysis" />
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminUserAnalytics;
